/**
 * Coherent-IA Task 2: the `content-widgets` build entry — a small,
 * progressive-enhancement JS bundle the STATIC generated content pages
 * (`scripts/gen-content/*`, served as plain HTML with no app JS of their
 * own) opt into via `<script type="module" src="...">`, its content-hashed
 * output filename resolved from Vite's build manifest at content-gen time
 * (see `scripts/gen-content/run.ts`'s `resolveContentWidgetsSrc`).
 * Static content stays crawlable/functional with JS off; this bundle only
 * ever ENHANCES it (adds interactivity), never changes what's already
 * rendered.
 *
 * Deliberately tiny: on `DOMContentLoaded`, scan for widget containers by
 * data attribute and hand each off to its own hydrator. Today that's
 * `[data-raga-scale]` (the raga-scale player, `raga-scale-widget.ts`) and
 * `[data-tanpura-widget]` (the online tanpura on `/tanpura/`,
 * `tanpura-widget.ts`) — another future widget is one more `data-*` scan +
 * import here, not a framework. One shared `TonePlayer` (`tone.ts` — the
 * SAME module the in-app raga rail plays through, no duplicated audio code)
 * is created once and passed to every raga-scale widget on the page, so a
 * tap on one cuts off playback from another. The tanpura widget owns its
 * own `TanpuraSynth` instance instead (a raga-scale tap and the tanpura
 * drone are deliberately independent sound sources, never expected to share
 * one player/context — see `tanpura-widget.ts`'s own doc).
 */
import { createTonePlayer } from '../audio/tone.js';
import { hydrateRagaScale } from './raga-scale-widget.js';
import { hydrateTanpuraWidget } from './tanpura-widget.js';

function hydrate(): void {
  const ragaScaleContainers = document.querySelectorAll<HTMLElement>('[data-raga-scale]');
  if (ragaScaleContainers.length > 0) {
    const tonePlayer = createTonePlayer();
    for (const container of ragaScaleContainers) hydrateRagaScale(container, tonePlayer);
  }

  const tanpuraContainers = document.querySelectorAll<HTMLElement>('[data-tanpura-widget]');
  for (const container of tanpuraContainers) hydrateTanpuraWidget(container);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrate);
} else {
  // Script is `type="module"` (deferred by spec) and placed at the end of
  // `<body>` in the generated pages, so DOMContentLoaded has almost always
  // already fired by the time this runs — handle that case directly instead
  // of missing the event.
  hydrate();
}
